import { useEffect, useRef, useState, useCallback } from "react";
import type { CSSProperties } from "react";
import { motion } from "framer-motion";

interface ChatButtonProps {
  onClick: () => void;
}

export const ChatButton = ({ onClick }: ChatButtonProps) => {
  const [liftPx, setLiftPx] = useState(0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const rafRef = useRef<number>(0);

  const computeLift = useCallback(() => {
    const footer = document.getElementById("site-footer");
    if (!footer) return 0;
    const footerRect = footer.getBoundingClientRect();
    const viewportH = window.innerHeight;

    // How much of the footer is visible from the bottom of the viewport
    const footerVisiblePx = Math.max(0, viewportH - footerRect.top);

    if (footerVisiblePx <= 0) return 0;

    // Lift chat fully above the visible portion of the footer + 16px breathing room
    const raw = footerVisiblePx + 16;
    // Clamp so it doesn't fly into the middle of the screen
    return Math.min(220, raw);
  }, []);

  useEffect(() => {
    const footer = document.getElementById("site-footer");
    if (!footer) return;

    const update = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setLiftPx(computeLift());
      });
    };

    // IntersectionObserver to know when footer is near
    observerRef.current = new IntersectionObserver(
      () => update(),
      { threshold: [0, 0.01, 0.1, 0.25, 0.5, 0.75, 1] },
    );
    observerRef.current.observe(footer);

    // Also update on scroll & resize for pixel-perfect tracking
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });

    return () => {
      observerRef.current?.disconnect();
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      cancelAnimationFrame(rafRef.current);
    };
  }, [computeLift]);

  const ease = [0.22, 1, 0.36, 1] as const;

  return (
    <motion.div
      className="fixed z-[200] right-4 md:right-6 pointer-events-auto"
      style={{
        bottom: `calc(env(safe-area-inset-bottom, 0px) + 16px + ${liftPx}px)`,
        transition: "bottom 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
      }}
      initial={{ opacity: 0, scale: 0.98, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: "tween", duration: 0.28, ease }}
    >
      {/* Stable hit-area wrapper (never moves) */}
      <div className="relative" style={{ padding: 10 }}>
        <motion.button
          onClick={onClick}
          aria-label="Open chat"
          whileHover={{ y: -2, scale: 1.02, ["--p" as any]: 1 }}
          whileTap={{ scale: 0.985, y: -1 }}
          transition={{ type: "tween", duration: 0.22, ease }}
          style={
            {
              width: 56,
              height: 56,
              borderRadius: 22,
              position: "relative",
              border: "none",
              padding: 0,
              cursor: "pointer",
              outline: "none",
              background: "transparent",
              WebkitTapHighlightColor: "transparent",
              willChange: "transform",
              backdropFilter: "saturate(1.25) contrast(1.12)",
              ["--p" as any]: 0.6,
            } as CSSProperties
          }
        >
          {/* Deep contact shadow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              boxShadow: "0 26px 60px rgba(0,0,0,0.62), 0 10px 22px rgba(0,0,0,0.38)",
              opacity: 0.9,
            }}
          />

          {/* Clear glass body */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              background: "rgba(255,255,255,0.045)",
              border: "1px solid rgba(255,255,255,0.16)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -18px 26px rgba(0,0,0,0.28)",
              overflow: "hidden",
            }}
          />

          {/* Edge thickness / refraction feel */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              background:
                "radial-gradient(140% 140% at 50% 50%, rgba(255,255,255,0) 52%, rgba(255,255,255,0.12) 74%, rgba(0,0,0,0.22) 100%)",
              opacity: 0.55,
              mixBlendMode: "normal",
            }}
          />

          {/* Single clean rim */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
              opacity: 0.65,
            }}
          />

          {/* Pink LED icon glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              background:
                "radial-gradient(70% 70% at 50% 58%, rgba(255,80,220,0.16) 0%, rgba(255,80,220,0.06) 38%, rgba(255,80,220,0) 66%)",
              opacity: "calc(0.55 + var(--p) * 0.35)",
              mixBlendMode: "screen",
            }}
          />

          {/* Icon */}
          <div className="absolute inset-0 grid place-items-center" style={{ borderRadius: 22 }}>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255, 95, 220, 0.98)"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                filter: "drop-shadow(0 0 10px rgba(255,95,220,0.55)) drop-shadow(0 0 20px rgba(255,95,220,0.28))",
              }}
            >
              <path d="M21 15a4 4 0 0 1-4 4H9l-6 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            </svg>
          </div>
        </motion.button>
      </div>
    </motion.div>
  );
};
